import React, { useState } from 'react';

import { Button, CopyIcon, SuccessIcon } from '@circleci/compass';
import { theme } from '@circleci/compass/dist/theme';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import yamlSyntax from 'react-syntax-highlighter/dist/cjs/languages/hljs/yaml';

import FormattedCodeStyler from '~/components/shared/FormattedCodeStyler';
import { styled } from '~/styled';

SyntaxHighlighter.registerLanguage('yaml', yamlSyntax);

interface FormattedCodeProps {
  language: string;
  showLineNumbers?: boolean;
  showCopyButton?: boolean;
  onCopyButtonClicked?: () => void | undefined;
  value?: string;
  children?: any;
}

const CopyButton = styled(Button)`
  position: inherit;
  float: right;
  padding: 8px 16px;
  border-radius: 4px;
  &:hover:enabled {
    cursor: pointer;
  }
  border: none;
  font-family: 'Roboto', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  cursor: pointer;
  position: absolute;
  right: 6px;
  top: 7px;
  z-index: 1;
`;

const CopyContainer = styled.div`
  position: relative;
  .formatted-code-btn {
    background: white;
    transition: opacity 0.4s ease;
    opacity: 0;
  }
  &:hover {
    .formatted-code-btn {
      opacity: 1;
    }
  }
`;

const DefaultContainer = styled.div`
  display: flex;
  border-radius: ${theme.space.space4};
  background-color: ${theme.colors.n40};
  width: 100%;
  margin-top: 0px;
  margin-bottom: 0px;
  padding-top: ${theme.space.space16};
  padding-bottom: ${theme.space.space16};
  padding-left: ${theme.space.space8};
  code {
    display: block;
  }
  pre {
    margin: 0;
  }
`;

const CodeContainer = styled.div`
  position: relative;
  display: flex;
  border: 1px solid ${theme.colors.n60};
  border-radius: ${theme.space.space4};
  width: 100%;
  line-height: normal;
  overflow-x: auto;
  code:nth-of-type(2) {
    display: block;
    padding-top: ${theme.space.space16};
  }
`;

const CodeTagOverrideElement = styled.div`
  > span:nth-of-type(1) {
    display: inline-block;
    padding-top: 16px;
  }
`;

// @ts-ignore
const StyledSyntaxHighlighter = styled(SyntaxHighlighter)`
  margin-top: 0;
  margin-bottom: 0;
  width: 0px;
  white-space: pre;
  .react-syntax-highlighter-line-number {
    background-color: ${theme.colors.n40};
    padding-left: ${theme.space.space20};
    padding-right: ${theme.space.space20};
    line-height: normal;
    display: block;
    justify-content: right;
    text-align: right;
  }
  .react-syntax-highlighter-line-number:nth-of-type(1) {
    padding-top: ${theme.space.space16};
    border-top-left-radius: ${theme.space.space4};
  }
  .react-syntax-highlighter-line-number:nth-last-of-type(1) {
    padding-bottom: ${theme.space.space16};
    border-bottom-left-radius: ${theme.space.space4};
  }

  div[class^='language-'] {
    height: 100%;
  }

  code {
    background-color: ${theme.colors.n40};
    padding-right: 0px !important;
    margin-right: 20px;
    height: 100%;
  }
`;

const CopyOnClick = (
  value: string,
  setIsCopied: React.Dispatch<React.SetStateAction<boolean>>,
): void => {
  const el = document.createElement('textarea');
  el.value = value;
  document.body.appendChild(el);
  el.select();
  document.execCommand('Copy');
  document.body.removeChild(el);

  // change the copy button icon on click.
  setIsCopied(true);
  setTimeout(() => {
    setIsCopied(false);
  }, 1500);
};

const FormattedCode = ({
  language,
  showLineNumbers = true,
  showCopyButton = true,
  onCopyButtonClicked,
  value,
  children,
}: FormattedCodeProps): JSX.Element => {
  const [isCopied, setIsCopied] = useState(false);
  const content = value ? value : children;

  return language === 'yaml' ? (
    <CopyContainer data-testid="formatted-code">
      {showCopyButton && (
        <CopyButton
          variation="flat"
          aria-label="Copy code"
          data-testid="copy-code-btn"
          onClick={(): void => {
            CopyOnClick(content, setIsCopied);
            if (onCopyButtonClicked !== undefined) {
              onCopyButtonClicked();
            }
          }}
          className="formatted-code-btn"
        >
          {isCopied ? (
            <SuccessIcon color="black" size={18} aria-hidden="true" />
          ) : (
            <CopyIcon size={18} aria-hidden="true" />
          )}
        </CopyButton>
      )}
      <CodeContainer>
        <StyledSyntaxHighlighter
          language={language}
          // there are a number of bugs around this - and an exception is raised around this prop, see linestyle.css
          // lineNumberStyle={{ color: 'red' }}
          // lineNumberStyle={() => { const style = { code: { style: { color: 'red' } } }; return style;  }}
          style={FormattedCodeStyler}
          showInlineLineNumbers={false}
          showLineNumbers={showLineNumbers}
          wrapLines={false}
          wrapLongLines={false}
          CodeTag={CodeTagOverrideElement}
        >
          {content}
        </StyledSyntaxHighlighter>
      </CodeContainer>
    </CopyContainer>
  ) : (
    <DefaultContainer>
      <pre>
        <code>{content}</code>
      </pre>
    </DefaultContainer>
  );
};

export default FormattedCode;
